import type { ContextMenuAction } from '../../../explorer/models/TreeModel';
import type { CapabilityState, StorageCapability } from '../../../../types/azureStorage';
import { STORAGE_READ_ONLY_REASON } from '../../../../core/engines/azureStorageRefusal';
import type { StorageMenuNode } from '../../../database-objects/services/storageContextMenuService';

export type StorageS3MenuContext = {
  readOnly: boolean;
  capabilities: Partial<Record<StorageCapability, CapabilityState>>;
};

function disabled(capability: CapabilityState | undefined): Pick<ContextMenuAction, 'disabled' | 'disabledReason'> {
  return capability?.state === 'unsupported' || capability?.state === 'unavailable'
    ? { disabled: true, disabledReason: capability.reason }
    : {};
}

function sasState(ctx: StorageS3MenuContext): Pick<ContextMenuAction, 'disabled' | 'disabledReason'> {
  const service = ctx.capabilities['service-sas'];
  const delegation = ctx.capabilities['user-delegation-sas'];
  if (service?.state === 'supported' || delegation?.state === 'supported') return {};
  return disabled(service ?? delegation);
}

export function s3MenuItems(node: StorageMenuNode, ctx: StorageS3MenuContext): ContextMenuAction[] {
  const kind = node.metadata.resource.kind;
  if (node.type === 'storage-service-folder' || kind === 'service') return [];
  // Stored access policies open on every policy-bearing resource, view-only on a read-only connection (the dialog
  // disables its controls with the read-only reason); only the capability can refuse the item (follow-up 2026-09-25).
  // F4 (last UI/UX pass): the share menu's glyphs (`lock`, `key`) — `shield` / `key-round` were not in the tree's map.
  const policies: ContextMenuAction = { id: 's3-access-policies', label: 'Manage access policies…', icon: 'lock', ...disabled(ctx.capabilities['access-policies']) };
  if (kind === 'table' || kind === 'queue') {
    return [policies, { id: 's3-sas', label: 'Get SAS…', icon: 'key', ...sasState(ctx) }];
  }
  if (kind !== 'container') return [];
  const writeDisabled = ctx.readOnly ? { disabled: true, disabledReason: STORAGE_READ_ONLY_REASON } : undefined;
  return [
    { id: 's3-properties', label: 'Properties', icon: 'info' },
    { id: 's3-public-access', label: 'Set public access level…', icon: 'globe', ...(writeDisabled ?? disabled(ctx.capabilities['container-acl'])) },
    policies,
    { id: 's3-sas', label: 'Get SAS…', icon: 'key', ...sasState(ctx) },
  ];
}
